import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Navbar, Nav, NavDropdown } from "react-bootstrap";
import { FaBars, FaBell, FaSignOutAlt, FaUser } from "react-icons/fa";
import { useStudentSession } from "../context/StudentSessionContext";
import { getStudentNotifications } from "../../../services/StudentPortal/StudentNotificationService";
import { STUDENT_STORAGE_KEYS } from "../../../services/StudentPortal/studentStorageKeys";
import ChildSwitcher from "./ChildSwitcher";
import ThemeColorPicker from "./ThemeColorPicker";

// Every class here is StudentPortal-only (`sp-header-*`). This previously
// reused the staff header's class names (`header-upper`, `left`/`right`,
// `header-icon-btn`, `navbar-main`, ...), which main.css styles with real,
// unscoped rules tuned for the staff layout — that's why the header looked
// broken. See StudentPortalLayout.js/StudentSidebar.js for the same fix
// applied to the rest of the shell.
const StudentHeader = () => {
  const { student, logout } = useStudentSession();
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;
    getStudentNotifications()
      .then((response) => {
        if (!isMounted) return;
        const data = response?.data?.data;
        setNotifications(Array.isArray(data) ? data : data?.notifications || []);
      })
      .catch(() => {
        // Non-fatal — the bell just stays empty if this fails.
      });
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // Restore the student's last icon-only/expanded sidebar choice (desktop
  // only — mobile always starts closed, see the off-canvas CSS).
  useEffect(() => {
    const shell = document.querySelector(".student-portal-shell");
    if (!shell || window.matchMedia("(max-width: 900px)").matches) return;
    if (localStorage.getItem(STUDENT_STORAGE_KEYS.SIDEBAR_COLLAPSED) === "true") {
      shell.classList.add("sp-sidebar-collapsed");
    }
  }, []);

  // Toggles a class on this module's own shell root (not the global
  // `document.body`, which the staff layout also keys off of with its own,
  // different-looking collapsed-sidebar rules). Below 900px this slides the
  // sidebar in/out (off-canvas); at desktop widths the same button instead
  // collapses it down to icon-only, remembered for next time.
  const toggleSidebar = () => {
    const shell = document.querySelector(".student-portal-shell");
    if (!shell) return;
    if (window.matchMedia("(max-width: 900px)").matches) {
      shell.classList.toggle("sp-sidebar-open");
      return;
    }
    const isCollapsed = shell.classList.toggle("sp-sidebar-collapsed");
    localStorage.setItem(STUDENT_STORAGE_KEYS.SIDEBAR_COLLAPSED, String(isCollapsed));
  };

  return (
    <div className="sp-header">
      <div className="sp-header-left">
        <button type="button" className="sp-header-icon-btn" onClick={toggleSidebar} title="Toggle sidebar">
          <FaBars size={18} />
        </button>
        <ChildSwitcher />
      </div>

      <div className="sp-header-right">
        <ThemeColorPicker />

        <div className="sp-header-notif" ref={dropdownRef}>
          <button
            type="button"
            className="sp-header-icon-btn"
            onClick={() => setShowNotifications((prev) => !prev)}
            title="Notifications"
          >
            <FaBell size={18} />
            {notifications.length > 0 && <span className="sp-header-notif-badge">{notifications.length}</span>}
          </button>

          {showNotifications && (
            <div className="sp-header-notif-panel">
              <div className="sp-header-notif-panel-title">Notifications</div>
              <ul className="sp-header-notif-list">
                {notifications.length > 0 ? (
                  notifications.slice(0, 8).map((notification) => (
                    <li
                      key={notification.id}
                      className="sp-header-notif-item"
                      onClick={() => navigate("/student-portal/notifications")}
                    >
                      {notification.message || notification.title}
                    </li>
                  ))
                ) : (
                  <li className="sp-header-notif-empty">No new notifications</li>
                )}
              </ul>
            </div>
          )}
        </div>

        <Navbar bg="none" expand="md" className="sp-header-navbar">
          <Navbar.Collapse>
            <Nav>
              <NavDropdown
                title={`Hi, ${
                  student?.full_name ||
                  [student?.first_name, student?.last_name].filter(Boolean).join(" ") ||
                  "Student"
                }`}
                id="student-nav-dropdown"
                className="sp-header-user-dropdown"
              >
                <NavDropdown.Item as={Link} to="/student-portal/profile">
                  <FaUser className="mr-2" /> Profile
                </NavDropdown.Item>
                <NavDropdown.Item onClick={logout}>
                  <FaSignOutAlt className="mr-2" /> Logout
                </NavDropdown.Item>
              </NavDropdown>
            </Nav>
          </Navbar.Collapse>
        </Navbar>
      </div>
    </div>
  );
};

export default StudentHeader;
